import React, { useState, useRef, useEffect } from "react";
import { makeStyles } from "tss-react/mui";
import { Box, InputBase, IconButton, Tooltip, useTheme } from "@mui/material";
import { KeyboardVoice, SendRounded } from "@mui/icons-material";
import { enqueueSnackbar } from "notistack";
import { Audio } from "../../../AI_Search/AI_Search/components/QueryBox/Audio";
import {
  CLICK_ON_RECORD_AUDIO_BUTTON,
  SUBMIT_SEARCH_QUERY,
} from "../../../../utils/posthogEvents";
import { usePostHog } from "posthog-js/react";
import ThinkingModeChip from "../../../AI_Search/AI_Search/components/QueryBox/ThinkingModeChip";

const useStyles = makeStyles()((theme) => ({
  container: {
    width: "100%",
    borderRadius: 12,
    padding: 12,
    border: `1px solid ${theme.palette.divider}`,
    background: theme.palette.background.default,
    boxShadow: "0px 2px 12px 1px rgba(0, 0, 0, 0.08)",
    "&:focus-within": {
      borderColor:
        theme.palette.mode === "dark" ? theme.palette.primary.main : "#9C27B0",
    },
  },
  activeSendBtn: {
    borderRadius: 8,
    cursor: "pointer",
    background: theme.palette.light,
  },
  disabledSendBtn: {
    borderRadius: 8,
    marginLeft: 12,
    cursor: "not-allowed",
    "&:hover": {
      backgroundColor: "transparent",
    },
  },
}));

function QueryBox({
  isStreaming = false,
  handleQuery = () => {},
  queryBoxContainerRef,
  isNewSession = false,
}) {
  const theme = useTheme();
  const posthog = usePostHog();
  const queryRef = useRef(null);
  const [audioMode, setAudioMode] = useState(false);
  const [query, setQuery] = useState("");
  const { classes } = useStyles();
  const [isInputFocused, setIsInputFocused] = useState(true);

  useEffect(() => {
    focusQueryBox();
  }, [queryRef]);

  const handleQueryChange = (e) => {
    setQuery(e.target.value);
  };

  const handleInputFocus = () => {
    setIsInputFocused(true);
  };

  const handleInputBlur = () => {
    setIsInputFocused(false);
  };

  const handleQuerySubmit = () => {
    const queryValue = query.trim();
    if (!queryValue) return;
    setQuery("");
    handleQuery(queryValue);
    posthog.capture(SUBMIT_SEARCH_QUERY);
  };

  const handleQueryKeyDown = (e) => {
    const { key, shiftKey } = e;
    const queryValue = query.trim();
    if (key === "Enter" && !shiftKey && queryValue && !isStreaming) {
      e.preventDefault();
      handleQuerySubmit();
    }
  };

  const focusQueryBox = () => {
    if (queryRef.current) {
      queryRef.current.focus();
    }
  };

  const handleClickRecordAudio = () => {
    posthog.capture(CLICK_ON_RECORD_AUDIO_BUTTON);
    setAudioMode(true);
  };

  return (
    <Box
      width={"100%"}
      ref={queryBoxContainerRef}
      className={classes.container}>
      <Box>
        {audioMode ? (
          <Box
            height={66}
            width={"100%"}
            mr={1}
            sx={{
              padding: 0.5,
              paddingBottom: 2,
            }}>
            <Audio
              recordingError={(e) => {
                setAudioMode(false);
                enqueueSnackbar("Permission denied", { variant: "error" });
              }}
              recordingCancelled={(e) => {
                setAudioMode(false);
                enqueueSnackbar("Voice query cancelled", {
                  variant: "warning",
                });
              }}
              recordingText={(text) => {
                setAudioMode(false);
                if (text) {
                  setQuery(text["text"]);
                } else {
                  enqueueSnackbar("Error processing voice query", {
                    variant: "error",
                  });
                }
              }}
            />
          </Box>
        ) : (
          <InputBase
            inputRef={queryRef}
            fullWidth
            value={query}
            onChange={handleQueryChange}
            onKeyDown={handleQueryKeyDown}
            onFocus={handleInputFocus}
            onBlur={handleInputBlur}
            placeholder={
              isNewSession
                ? "Search using PID no or Equipment tag..."
                : "Ask a follow-up..."
            }
            multiline
            // minRows={isInputFocused ? 3 : 2}
            minRows={2}
            maxRows={3}
            autoFocus
            sx={{
              paddingX: 0.5,
              paddingBottom: 2,
            }}
            slotProps={{
              input: {
                sx: {
                  // transition: "height 0.3s ease-in-out",
                  "&::placeholder": {
                    color: theme.palette.text.secondary,
                    opacity: 1,
                  },
                },
              },
            }}
          />
        )}
        <Box
          display={"flex"}
          alignItems={"center"}
          justifyContent={"space-between"}>
          <ThinkingModeChip />
          {query ? (
            <IconButton
              size="small"
              onClick={handleQuerySubmit}
              disabled={!(query && !isStreaming)}
              className={
                query && !isStreaming
                  ? classes.activeSendBtn
                  : classes.disabledSendBtn
              }>
              <SendRounded
                fontSize="small"
                sx={{
                  color:
                    !query || isStreaming
                      ? "inherit"
                      : theme.palette.background.default,
                }}
              />
            </IconButton>
          ) : (
            <IconButton
              onClick={handleClickRecordAudio}
              disabled={audioMode || isStreaming}
              size="small">
              <Tooltip title="Speak your question">
                <KeyboardVoice
                  fontSize="small"
                  sx={{ color: theme.palette.text.primary }}
                />
              </Tooltip>
            </IconButton>
          )}
        </Box>
      </Box>
    </Box>
  );
}

export default QueryBox;
